import { ENV } from "../../config/runtimeEnv";

import { CatalogueVariant, VariantCataloguePage, VariantCatalogueParams } from "../../types/geneCodes/VariantCatalogueTypes";
import { http } from "../../lib/http/client";

// Already ends in `/api` — a second one 404s the request as /api/api/catalogue/variants.
const API_BASE_URL = ENV.VITE_LABIQ_SERVER || "";

// The endpoint 422s without one of these — "never ->get() a reference table without a where()", enforced.
export const SELECTIVE_FILTERS = ["trait", "gene", "rs", "efo", "term"] as const;

export const hasSelectiveFilter = (params: VariantCatalogueParams): boolean =>
	SELECTIVE_FILTERS.some((key) => typeof params[key] === "string" && (params[key] as string).trim() !== "");

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((entry) => typeof entry === "string");

const reject = (detail: string): never => {
	throw new Error(`Unexpected variant catalogue response: ${detail}.`);
};

const asVariant = (value: unknown, index: number): CatalogueVariant => {
	const row = value as Record<string, unknown> | null;
	if (!row || typeof row !== "object") reject(`variant ${index} is not an object`);

	const terms = (row as Record<string, unknown>).terms as Record<string, unknown> | undefined;
	const membership = (row as Record<string, unknown>).membership as Record<string, unknown> | undefined;

	if (!terms || !isStringArray(terms.efo) || !isStringArray(terms.mesh) || !isStringArray(terms.hpo) || !isStringArray(terms.do)) {
		reject(`variant ${index} has no {efo, mesh, hpo, do} terms`);
	}
	if (!membership || typeof membership.in_current_panel !== "boolean" || !isStringArray(membership.panels)) {
		reject(`variant ${index} has no {in_current_panel, panels} membership`);
	}
	if (!isStringArray((row as Record<string, unknown>).pmids)) reject(`variant ${index} has no pmids array`);

	return row as unknown as CatalogueVariant;
};

/**
 * EX-lane precedent: a consumer that fell back to `[]` on an unrecognised body turned every 200 into
 * "no data", with green unit tests on both sides because each mocked the other. An unexpected shape
 * throws here so the pane can say so, and can never quietly render an empty catalogue.
 */
const asPage = (body: unknown): VariantCataloguePage => {
	const envelope = body as Record<string, unknown> | null;
	if (!envelope || envelope.success !== true) reject("`success` is not true");

	const data = (envelope as Record<string, unknown>).data as Record<string, unknown> | undefined;
	if (!data || typeof data !== "object") reject("`data` is not an object");
	if (!Array.isArray((data as Record<string, unknown>).variants)) reject("`data.variants` is not an array");

	const { total, page, per_page: perPage } = data as { total: unknown; page: unknown; per_page: unknown };
	if (typeof total !== "number" || typeof page !== "number" || typeof perPage !== "number") {
		reject("`data.total`, `data.page` and `data.per_page` are not all numbers");
	}

	return {
		variants: ((data as Record<string, unknown>).variants as unknown[]).map(asVariant),
		total: total as number,
		page: page as number,
		per_page: perPage as number,
	};
};

/** The GWAS catalogue behind the builder's research pane. Requires one of SELECTIVE_FILTERS. */
export const fetchVariantCatalogue = async (params: VariantCatalogueParams): Promise<VariantCataloguePage> => {
	if (!hasSelectiveFilter(params)) throw new Error(`One of ${SELECTIVE_FILTERS.join(", ")} is required.`);

	const response = await http.get(`${API_BASE_URL}/catalogue/variants`, {
		params: {
			...(params.trait ? { trait: params.trait } : {}),
			...(params.gene ? { gene: params.gene } : {}),
			...(params.rs ? { rs: params.rs } : {}),
			...(params.efo ? { efo: params.efo } : {}),
			...(params.term ? { term: params.term } : {}),
			...(params.min_p != null ? { min_p: params.min_p } : {}),
			...(params.population ? { population: params.population } : {}),
			// `exists:panels,id_panels` — an unknown id is a 422, so it is omitted rather than sent as null.
			...(params.panel_id != null ? { panel_id: params.panel_id } : {}),
			...(params.page != null ? { page: params.page } : {}),
			...(params.per_page != null ? { per_page: params.per_page } : {}),
		},
	});

	return asPage(response.data);
};
